import { useLocale } from "../../../context/LanguageContext";
import { NOTIFICATION_TYPE_LABEL_KEYS } from "../constants";
import { VariableChip } from "./VariableChip";
import type { TriggerContract, NotificationType } from "../types";

interface TriggerContractsTableProps {
  contracts: TriggerContract[];
  loading: boolean;
}

export function TriggerContractsTable({ contracts, loading }: TriggerContractsTableProps) {
  const { t } = useLocale();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">{t("notifications.contracts.loading")}</p>
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">{t("notifications.contracts.empty")}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{t("notifications.contracts.colTemplateName")}</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{t("notifications.contracts.colDescription")}</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{t("notifications.contracts.colType")}</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{t("notifications.contracts.colProvidedVariables")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {contracts.map((c) => (
            <tr key={c.templateName} className="hover:bg-muted/50">
              <td className="px-4 py-3">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
                  {c.templateName}
                </code>
              </td>
              <td className="max-w-xs px-4 py-3 text-muted-foreground">{c.description}</td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  {t(NOTIFICATION_TYPE_LABEL_KEYS[c.notificationType as NotificationType] ?? c.notificationType)}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                  {c.providedVariables.map((v) => (
                    <VariableChip
                      key={v.name}
                      name={v.name}
                      type={v.type}
                      description={v.description}
                      example={v.example}
                      compact
                    />
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
